import React, { useState, useEffect, useCallback, useRef } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import Form from 'react-bootstrap/Form';
import { PencilFill, TrashFill, XLg, ClockHistory, Download } from 'react-bootstrap-icons';

import {
  RequirementTestDetailResponse,
  RequirementTestRunEntry,
  TestCaseVersion,
  UpdateTestCasePayload,
} from '../../types/requirements';
import {
  deleteRequirementTestCase,
  updateTestCase,
  fetchTestCaseVersions,
} from '../../api/requirements';

import apiClient, { API_ENDPOINTS } from '../../api/config';
import { formatFolderPathForDisplay } from './FolderPathSelector';

type PopupTab = 'summary' | 'steps' | 'linked' | 'data' | 'chat' | 'history';

interface TestCasePopupProps {
  testKey: string;
  projectId: number | string;
  generationId: number | string;
  artifactId: number | string;
  detail?: RequirementTestDetailResponse | null;
  detailLoading: boolean;
  detailError?: string | null;
  onClose: () => void;
  onDeleted: (testKey: string) => void;
  onEdited: (testKey: string, updatedTestCase: Record<string, any>) => void;
  chatPanel?: React.ReactNode;
  adoTenant?: string | null;
  adoProjectName?: string | null;
  isSyntheticMode?: boolean;
}

interface EditState {
  auth_mode: '' | 'authenticated' | 'unauthenticated';
  title: string;
  description: string;
  steps: Array<{ step_id?: number | string; action: string; expected_result: string }>;
  priority: string;
}

function getRelevantDatasetColumns(dataset: { columns?: string[]; rows?: Record<string, unknown>[] }): string[] {
  const declaredColumns = Array.isArray(dataset.columns) ? dataset.columns : [];
  const rows = Array.isArray(dataset.rows) ? dataset.rows : [];
  const preferred = ['test_case_id', 'display_id'];
  const hasMeaningfulValue = (value: unknown): boolean => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim() !== '';
    return true;
  };

  return declaredColumns.filter((column) => {
    if (!column) return false;
    if (preferred.includes(column)) {
      return rows.some((row) => hasMeaningfulValue(row?.[column]));
    }
    return rows.some((row) => hasMeaningfulValue(row?.[column]));
  });
}

function renderRunBadge(run?: RequirementTestRunEntry | null): React.ReactNode {
  if (!run) {
    return <span className="rrw-status-pill rrw-status-pill-never_run">Never Run</span>;
  }
  return (
    <span className={`rrw-status-pill rrw-status-pill-${run.status}`}>
      {run.status.replace(/_/g, ' ')}
    </span>
  );
}

function TestCasePopup({
  testKey,
  projectId,
  generationId,
  artifactId,
  detail,
  detailLoading,
  detailError,
  onClose,
  onDeleted,
  onEdited,
  chatPanel,
  adoTenant,
  adoProjectName,
  isSyntheticMode = false,
}: TestCasePopupProps): React.ReactElement {
  const [activeTab, setActiveTab] = useState<PopupTab>('summary');
  const [editMode, setEditMode] = useState(false);
  const [editFields, setEditFields] = useState<EditState>({ title: '', description: '', steps: [], priority: '', auth_mode: '' });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [versions, setVersions] = useState<TestCaseVersion[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsError, setVersionsError] = useState<string | null>(null);
  const versionsRequestRef = useRef(0);
  const [dataVariants, setDataVariants] = useState<any[]>([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError, setDataError] = useState<string | null>(null);

  // Seed edit fields when detail loads
  useEffect(() => {
    if (detail?.test) {
      const t = detail.test;
      setEditFields({
        title: t.title || '',
        description: t.description || '',
        steps: (t.steps || []).map((s: any) => ({
          step_id: s.step_id,
          action: s.action || s.step || '',
          expected_result: s.expected_result || s.expected || '',
        })),
        priority: t.priority != null ? String(t.priority) : '',
        auth_mode: t.auth_mode || '',
      });
    }
  }, [detail]);

  // Never retain another test case's version rows while this popup is reused.
  // The database remains the source of truth; this only resets local display state.
  useEffect(() => {
    versionsRequestRef.current += 1;
    setVersions([]);
    setVersionsLoading(false);
    setVersionsError(null);
  }, [projectId, testKey]);

  // Load version history when tab is opened. Ignore late responses from a
  // previous test selection so v1 from one test cannot replace v3 of another.
  useEffect(() => {
    if (activeTab !== 'history') return;
    const requestId = ++versionsRequestRef.current;
    setVersionsLoading(true);
    setVersionsError(null);
    fetchTestCaseVersions(projectId, testKey)
      .then((receivedVersions) => {
        if (requestId !== versionsRequestRef.current) return;
        setVersions(
          [...(receivedVersions || [])].sort((a, b) => (
            Number(b.version_number || 0) - Number(a.version_number || 0)
          )),
        );
      })
      .catch(() => {
        if (requestId === versionsRequestRef.current) {
          setVersionsError('Failed to load version history.');
        }
      })
      .finally(() => {
        if (requestId === versionsRequestRef.current) {
          setVersionsLoading(false);
        }
      });
  }, [activeTab, projectId, testKey]);

  const handleDownloadVariantsCsv = useCallback((dataset: { dataset_name?: string; columns: string[]; rows: Record<string, unknown>[] }) => {
    const columns = getRelevantDatasetColumns(dataset);
    const rows = Array.isArray(dataset.rows) ? dataset.rows : [];
    if (!columns.length) return;

    const escapeCsv = (value: unknown): string => {
      const text = String(value ?? '');
      if (/[",\n]/.test(text)) {
        return `"${text.replace(/"/g, '""')}"`;
      }
      return text;
    };

    const lines = [
      columns.map(escapeCsv).join(','),
      ...rows.map((row) => columns.map((col) => escapeCsv(row?.[col])).join(',')),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const safeName = String(dataset.dataset_name || testKey || 'synthetic_variants').replace(/[^\w.-]+/g, '_');
    link.href = url;
    link.download = `${safeName}_${String(testKey || 'test_case').replace(/[^\w.-]+/g, '_')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [testKey]);

  // Load synthetic data variants when tab is opened
  useEffect(() => {
    if (activeTab !== 'data') return;
    setDataLoading(true);
    setDataError(null);
    apiClient.get(API_ENDPOINTS.TEST_DATA_CASE_VARIANTS(projectId, testKey))
      .then(res => setDataVariants(res.data))
      .catch(() => setDataError('Failed to load synthetic data variants.'))
      .finally(() => setDataLoading(false));
  }, [activeTab, projectId, testKey]);

  const handleStartEdit = useCallback(() => {
    setEditMode(true);
    setSaveError(null);
  }, []);

  const handleCancelEdit = useCallback(() => {
    if (detail?.test) {
      const t = detail.test;
      setEditFields({
        title: t.title || '',
        description: t.description || '',
        steps: (t.steps || []).map((s: any) => ({
          step_id: s.step_id,
          action: s.action || s.step || '',
          expected_result: s.expected_result || s.expected || '',
        })),
        priority: t.priority != null ? String(t.priority) : '',
        auth_mode: t.auth_mode || '',
      });
    }
    setEditMode(false);
    setSaveError(null);
  }, [detail]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const payload: UpdateTestCasePayload = {
        auth_mode: editFields.auth_mode || null,
        title: editFields.title || undefined,
        description: editFields.description || undefined,
        steps: editFields.steps.map((s, i) => ({
          step_id: s.step_id ?? i + 1,
          action: s.action,
          expected_result: s.expected_result,
        })),
        priority: editFields.priority !== '' ? editFields.priority : undefined,
      };
      const result = await updateTestCase(projectId, generationId, artifactId, testKey, payload);
      // Pass the full updated test case back so parent can update popup immediately
      onEdited(testKey, result);
      setEditMode(false);
    } catch {
      setSaveError('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [editFields, projectId, generationId, artifactId, testKey, onEdited]);

  const handleStepChange = useCallback(
    (index: number, field: 'action' | 'expected_result', value: string) => {
      setEditFields((prev) => {
        const updated = [...prev.steps];
        updated[index] = { ...updated[index], [field]: value };
        return { ...prev, steps: updated };
      });
    },
    [],
  );

  const handleAddStep = useCallback(() => {
    setEditFields((prev) => ({
      ...prev,
      steps: [...prev.steps, { action: '', expected_result: '' }],
    }));
  }, []);

  const handleRemoveStep = useCallback((index: number) => {
    setEditFields((prev) => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== index),
    }));
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteRequirementTestCase(projectId, generationId, artifactId, testKey);
      onDeleted(testKey);
    } catch {
      setDeleteError('Failed to delete the test case. Please try again.');
      setDeleting(false);
    }
  }, [projectId, generationId, artifactId, testKey, onDeleted]);

  const TABS: Array<{ id: PopupTab; label: string }> = [
    { id: 'summary', label: 'Summary' },
    { id: 'steps', label: 'Steps' },
    { id: 'linked', label: 'Requirement' },
    ...(isSyntheticMode ? [{ id: 'data' as const, label: 'Synthetic Data' }] : []),
    { id: 'chat', label: 'Test Case Analysis' },
    { id: 'history', label: 'Version History' },
  ];

  const test = detail?.test;
  const latestRun = detail?.latest_run;
  const linkedRequirement = detail?.linked_requirement;

  return (
    <Modal show onHide={onClose} size="lg" scrollable centered className="tc-popup-modal">
      <Modal.Header className="tc-popup-header" closeButton>
        <div className="tc-popup-header-content">
          {detailLoading ? (
            <Spinner animation="border" size="sm" />
          ) : test ? (
            <>
              <div className="tc-popup-kicker">{test.test_key}</div>
              {editMode ? (
                <Form.Control
                  className="tc-popup-title-input"
                  value={editFields.title}
                  onChange={(e) => setEditFields((p) => ({ ...p, title: e.target.value }))}
                  placeholder="Test case title"
                />
              ) : (
                <h5 className="tc-popup-title">{test.title || test.test_key}</h5>
              )}
              <div className="tc-popup-meta">
                {renderRunBadge(latestRun)}
                <span>{test.test_type || 'Unknown'} test</span>
                <span>{formatFolderPathForDisplay(test.folder_path || [])}</span>
              </div>
            </>
          ) : null}
        </div>
      </Modal.Header>

      {/* Action bar below title */}
      {!detailLoading && test && (
        <div className="tc-popup-action-bar">
          <div className="tc-popup-tabs">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`tc-popup-tab ${activeTab === tab.id ? 'tc-popup-tab-active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
                disabled={editMode && tab.id !== 'steps'}
              >
                {tab.id === 'history' && <ClockHistory size={12} className="me-1" />}
                {tab.label}
              </button>
            ))}
          </div>

          <div className="tc-popup-header-actions">
            {!editMode && !deleteConfirm && (
              <>
                <Button variant="outline-secondary" size="sm" onClick={handleStartEdit}>
                  <PencilFill size={11} className="me-1" />
                  Edit
                </Button>
                <Button variant="outline-danger" size="sm" onClick={() => setDeleteConfirm(true)}>
                  <TrashFill size={11} className="me-1" />
                  Delete
                </Button>

              </>
            )}
            {editMode && (
              <>
                <Button variant="outline-secondary" size="sm" onClick={handleCancelEdit} disabled={saving}>
                  Cancel
                </Button>
                <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
                  {saving ? <Spinner animation="border" size="sm" className="me-1" /> : null}
                  Save Changes
                </Button>
              </>
            )}
            {deleteConfirm && (
              <div className="tc-popup-delete-confirm">
                <span className="tc-popup-delete-msg">Delete this test case?</span>
                <Button variant="outline-secondary" size="sm" onClick={() => setDeleteConfirm(false)} disabled={deleting}>
                  Cancel
                </Button>
                <Button variant="danger" size="sm" onClick={handleDeleteConfirm} disabled={deleting}>
                  {deleting ? <Spinner animation="border" size="sm" className="me-1" /> : <TrashFill size={11} className="me-1" />}
                  Confirm Delete
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      <Modal.Body className="tc-popup-body">
        {!detailLoading && test && (
          <Form.Group className="mb-3" controlId="test-auth-mode">
            <Form.Label>Starting session</Form.Label>
            <Form.Select value={editMode ? editFields.auth_mode : (test.auth_mode || '')}
              disabled={!editMode || saving}
              onChange={(e) => setEditFields((p) => ({ ...p, auth_mode: e.target.value as EditState['auth_mode'] }))}>
              <option value="">Not set (existing behavior)</option>
              <option value="authenticated">Authenticated</option>
              <option value="unauthenticated">Unauthenticated</option>
            </Form.Select>
            <Form.Text>Authenticated starts logged in. Unauthenticated starts with an empty session.</Form.Text>
          </Form.Group>
        )}
        {saveError && <div className="tc-popup-error">{saveError}</div>}
        {deleteError && <div className="tc-popup-error">{deleteError}</div>}

        {detailLoading && (
          <div className="tc-popup-loading">
            <Spinner animation="border" />
          </div>
        )}

        {detailError && !detailLoading && (
          <div className="rrw-empty-panel">{detailError}</div>
        )}

        {!detailLoading && test && (
          <>
            {/* ── Summary ── */}
            {activeTab === 'summary' && (
              <div className="rrw-detail-section">
                <div className="rrw-stat-grid">
                  <div className="rrw-stat-card">
                    <div className="rrw-stat-label">Category</div>
                    <div className="rrw-stat-value">{test.category || 'Functional Testing'}</div>
                  </div>
                  <div className="rrw-stat-card">
                    <div className="rrw-stat-label">Sub-category</div>
                    <div className="rrw-stat-value">{test.sub_category || 'Form & Input Validation'}</div>
                  </div>
                  <div className="rrw-stat-card">
                    <div className="rrw-stat-label">Priority</div>
                    <div className="rrw-stat-value">
                      {editMode ? (
                        <Form.Control
                          size="sm"
                          value={editFields.priority}
                          onChange={(e) => setEditFields((p) => ({ ...p, priority: e.target.value }))}
                          placeholder="e.g. High"
                        />
                      ) : (
                        test.priority ?? 'N/A'
                      )}
                    </div>
                  </div>
                  <div className="rrw-stat-card">
                    <div className="rrw-stat-label">Automation</div>
                    <div className="rrw-stat-value">{test.automation_candidate || test.automation_status || 'Unknown'}</div>
                  </div>
                </div>

                <div className="rrw-summary-card">
                  <div className="rrw-summary-title">Description</div>
                  {editMode ? (
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={editFields.description}
                      onChange={(e) => setEditFields((p) => ({ ...p, description: e.target.value }))}
                      placeholder="Describe what this test case validates…"
                      className="tc-popup-textarea"
                    />
                  ) : (
                    <p className="rrw-summary-copy">{test.description || 'No description was provided for this test case.'}</p>
                  )}
                </div>

                {linkedRequirement && (
                  <div className="rrw-summary-card">
                    <div className="rrw-summary-title">Linked Requirement</div>
                    <div className="rrw-linked-pills">
                      <span className="rrw-linked-pill">
                        Epic: {linkedRequirement.epic?.title || 'Unknown'}
                      </span>
                      <span className="rrw-linked-pill">
                        Story: {linkedRequirement.user_story?.title || 'Unknown'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Steps ── */}
            {activeTab === 'steps' && (
              <div className="rrw-detail-section">
                {!editMode && (test.steps || []).length === 0 && (
                  <div className="rrw-empty-panel">No authored test steps are available.</div>
                )}
                {!editMode && (test.steps || []).map((step: any, index: number) => (
                  <div key={`${test.test_key}-step-${index + 1}`} className="rrw-step-card">
                    <div className="rrw-step-index">Step {step.step_id || index + 1}</div>
                    <div className="rrw-step-action">{step.action || step.step || 'No action provided'}</div>
                    <div className="rrw-step-expected">{step.expected_result || step.expected || 'No expected result'}</div>
                  </div>
                ))}

                {editMode && (
                  <div className="tc-popup-steps-editor">
                    {editFields.steps.map((step, index) => (
                      <div key={index} className="tc-popup-step-row">
                        <div className="tc-popup-step-num">Step {index + 1}</div>
                        <div className="tc-popup-step-fields">
                          <Form.Control
                            as="textarea"
                            rows={2}
                            placeholder="Action"
                            value={step.action}
                            onChange={(e) => handleStepChange(index, 'action', e.target.value)}
                            className="tc-popup-textarea mb-2"
                          />
                          <Form.Control
                            as="textarea"
                            rows={2}
                            placeholder="Expected result"
                            value={step.expected_result}
                            onChange={(e) => handleStepChange(index, 'expected_result', e.target.value)}
                            className="tc-popup-textarea"
                          />
                        </div>
                        <Button
                          variant="link"
                          size="sm"
                          className="tc-popup-step-remove"
                          onClick={() => handleRemoveStep(index)}
                          title="Remove step"
                        >
                          <XLg size={14} />
                        </Button>
                      </div>
                    ))}
                    <Button variant="outline-secondary" size="sm" onClick={handleAddStep} className="mt-2">
                      + Add Step
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* ── Linked ── */}
            {activeTab === 'linked' && (
              <div className="rrw-detail-section">
                {linkedRequirement ? (
                  <>
                    <div className="rrw-summary-card">
                      <div className="rrw-summary-title">Epic</div>
                      <div className="rrw-summary-heading">{linkedRequirement.epic?.title || 'Unknown epic'}</div>
                      <p className="rrw-summary-copy">{linkedRequirement.epic?.description || 'No epic description was provided.'}</p>
                    </div>
                    <div className="rrw-summary-card">
                      <div className="rrw-summary-title">User Story</div>
                      <div className="rrw-summary-heading">{linkedRequirement.user_story?.title || 'Unknown story'}</div>
                      <p className="rrw-summary-copy">{linkedRequirement.user_story?.description || 'No user story description was provided.'}</p>
                      {linkedRequirement.user_story?.acceptance_criteria && (
                        <div className="rrw-inline-note">{linkedRequirement.user_story.acceptance_criteria}</div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="rrw-empty-panel">No linked requirement information is available.</div>
                )}
                {/* Feature 8: source reference — shown for all test cases that have one */}
                {test.source_reference && (
                  <div className="rrw-summary-card">
                    <div className="rrw-summary-title">Source</div>
                    {test.source_reference.startsWith('ado:') && adoTenant && adoProjectName ? (
                      <a
                        className="rrw-source-link"
                        href={`https://dev.azure.com/${adoTenant}/${adoProjectName}/_workitems/edit/${test.source_reference.slice(4)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        ADO #{test.source_reference.slice(4)} ↗
                      </a>
                    ) : test.source_reference.startsWith('ado:') ? (
                      <span className="rrw-summary-copy">ADO #{test.source_reference.slice(4)}</span>
                    ) : (
                      <span className="rrw-summary-copy">{test.source_reference}</span>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ── Synthetic Data ── */}
            {activeTab === 'data' && isSyntheticMode && (
              <div className="rrw-detail-section">
                {dataLoading && (
                  <div className="tc-popup-loading">
                    <Spinner animation="border" size="sm" />
                    <span className="ms-2">Fetching data variants…</span>
                  </div>
                )}
                {dataError && !dataLoading && (
                  <div className="rrw-empty-panel text-danger">{dataError}</div>
                )}
                {!dataLoading && !dataError && dataVariants.length === 0 && (
                  <div className="rrw-empty-panel">No synthetic data variants have been generated for this test case yet.</div>
                )}
                {!dataLoading && dataVariants.map((ds, dsIndex) => {
                  const relevantColumns = getRelevantDatasetColumns(ds);
                  return (
                  <div key={ds.dataset_id} className="rrw-summary-card mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div className="rrw-summary-title">Dataset: {ds.dataset_name || `Dataset ${dsIndex + 1}`}</div>
                      <div className="d-flex align-items-center gap-2">
                        <span className="badge bg-light text-dark border">{ds.rows.length} Variants</span>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleDownloadVariantsCsv(ds)}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                        >
                          <Download size={13} />
                          CSV
                        </Button>
                      </div>
                    </div>
                    <div className="table-responsive rrw-data-table-wrapper" style={{ maxHeight: '300px' }}>
                      <table className="table table-sm table-bordered small" data-responsive-cards="true">
                        <thead className="table-light sticky-top">
                          <tr>
                            {relevantColumns.map((col: string) => (
                              <th key={col}>{col}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {ds.rows.map((row: any, rowIndex: number) => (
                            <tr key={rowIndex}>
                              {relevantColumns.map((col: string) => (
                                <td key={col} data-label={col}>{String(row[col] ?? '')}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}

            {/* ── Chat ── */}
            {activeTab === 'chat' && (
              <div className="rrw-detail-section">
                {chatPanel || <div className="rrw-empty-panel">Chat is unavailable for this test.</div>}
              </div>
            )}

            {/* ── Version History ── */}
            {activeTab === 'history' && (
              <div className="rrw-detail-section">
                {versionsLoading && (
                  <div className="tc-popup-loading">
                    <Spinner animation="border" size="sm" />
                    <span className="ms-2">Loading version history…</span>
                  </div>
                )}
                {versionsError && !versionsLoading && (
                  <div className="rrw-empty-panel">{versionsError}</div>
                )}
                {!versionsLoading && !versionsError && versions.length === 0 && (
                  <div className="rrw-empty-panel">No version history found for this test case.</div>
                )}
                {!versionsLoading && versions.map((v) => {
                  let snap: Record<string, any> | null = null;
                  try { snap = JSON.parse(v.snapshot); } catch { snap = null; }
                  const snapTitle = snap?.title || snap?.test_key || '—';
                  const snapDesc = snap?.description ? snap.description.slice(0, 120) + (snap.description.length > 120 ? '…' : '') : null;
                  const snapSteps = Array.isArray(snap?.steps) ? snap.steps.length : null;

                  return (
                    <div key={v.id} className="tc-popup-version-row">
                      <div className="tc-popup-version-badge">v{v.version_number}</div>
                      <div className="tc-popup-version-info">
                        <div className="tc-popup-version-label">
                          {v.change_type === 'user_edit'
                            ? 'Edited by user'
                            : `Generated by agent${v.generation_number ? ` (generation #${v.generation_number})` : ''}`}
                        </div>
                        <div className="tc-popup-version-date">
                          {new Date(v.created_at).toLocaleString()}
                        </div>
                        {v.version_status && (
                          <span className={`rrw-status-pill rrw-status-pill-${v.version_status}`}>
                            {v.version_status}
                          </span>
                        )}
                        {snap && (
                          <div className="tc-popup-version-snapshot">
                            <div className="tc-popup-version-snap-title">{snapTitle}</div>
                            {snapDesc && <div className="tc-popup-version-snap-desc">{snapDesc}</div>}
                            {snapSteps !== null && (
                              <div className="tc-popup-version-snap-meta">{snapSteps} step{snapSteps === 1 ? '' : 's'}</div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default TestCasePopup;
